/**
 * Extract bind defaults from an example by walking template + blockJson in parallel.
 *
 * Why: Channel 1 templates use `#{__bind.<key>.value}` placeholders that have no
 * `??` literal fallback. Without `resolverOverrides` from the caller, the binder
 * falls back to the generic schema-driven placeholder ("Sample heading",
 * "Sample text 0"). For first-shot dispatch (no user customization), this surfaces
 * placeholder strings to users. The corpus already has the original literals in
 * `blockJson` at the same tree position — this extractor surfaces them as
 * `__bind.<key>.value` defaults so the binder produces realistic output.
 *
 * Design:
 *  - Walk template + blockJson in lockstep (object: same keys, array: same indices)
 *  - When template leaf matches `#{__bind.<key>.value}` (with optional `?? <literal>`)
 *    and blockJson at the same position is a string, record { key.value: blockValue }
 *  - Trees that diverge structurally degrade gracefully (skip diverged subtree)
 *  - Returns empty when blockJson is null/undefined/non-object
 *
 * Path shape returned matches what `BindResolver.resolve(path)` receives — bare
 * "<key>.value" form, suitable for direct lookup by `default-resolvers.ts`.
 *
 * Issue #8 fix.
 */

const BIND_EXPR_RE = /^#\{__bind\.([^.}\s]+)\.value(\s*\?\?\s*[^}]*)?\}$/

export function extractBindDefaults(template: unknown, blockJson: unknown): Record<string, string> {
  const out: Record<string, string> = {}
  walk(template, blockJson, out)
  return out
}

function walk(tpl: unknown, block: unknown, out: Record<string, string>): void {
  // String leaf — check for bind expression match
  if (typeof tpl === "string") {
    if (typeof block !== "string") return
    const m = tpl.match(BIND_EXPR_RE)
    if (m) {
      out[`${m[1]}.value`] = block
    }
    return
  }

  // Array — walk in lockstep
  if (Array.isArray(tpl)) {
    if (!Array.isArray(block)) return
    const len = Math.min(tpl.length, block.length)
    for (let i = 0; i < len; i++) {
      walk(tpl[i], block[i], out)
    }
    return
  }

  // Object — walk shared keys
  if (tpl !== null && typeof tpl === "object") {
    if (block === null || typeof block !== "object" || Array.isArray(block)) return
    const tplObj = tpl as Record<string, unknown>
    const blockObj = block as Record<string, unknown>
    for (const key of Object.keys(tplObj)) {
      if (key in blockObj) {
        walk(tplObj[key], blockObj[key], out)
      }
    }
    return
  }
  // Other primitives (number, boolean, null) — nothing to extract
}
